import type { DayMetrics } from "./api.ts";
import { localDay } from "./time.ts";

/** The v0 exit criterion (docs/roadmap.md#metrics): completeness ≥ 95%. */
export const COMPLETENESS_TARGET = 95;
/** The most days the data API answers for at once (`metrics`). */
export const MAX_RANGE_DAYS = 31;
/** The default range's length. */
export const DEFAULT_RANGE_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The calendar day `days` before a `YYYY-MM-DD` date. */
export function dayBefore(date: string, days = 1): string {
  return new Date(Date.parse(`${date}T00:00:00.000Z`) - days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The default range: the two weeks ending yesterday, in the browser's local days. Today is not
 * over, and the daily job settles yesterday at 03:00 in Los Angeles.
 */
export function defaultRange(now = new Date()): { from: string; to: string } {
  const to = dayBefore(localDay(now));
  return { from: dayBefore(to, DEFAULT_RANGE_DAYS - 1), to };
}

/** Why a range cannot be asked for, or undefined when it can. */
export function rangeProblem(from: string, to: string): string | undefined {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end)) return "Choose both days";
  if (start > end) return "The first day is after the last";
  if ((end - start) / DAY_MS + 1 > MAX_RANGE_DAYS) return `At most ${MAX_RANGE_DAYS} days at once`;
  return undefined;
}

/** What the page says of the range's days beyond the data API's total. */
export interface DayCounts {
  /** Days whose own completeness is under the target. */
  readonly daysUnderTarget: number;
  /** Days with at least one active producer. */
  readonly daysInUse: number;
}

export function dayCounts(days: readonly DayMetrics[]): DayCounts {
  return {
    daysUnderTarget: days.filter(
      (day) =>
        day.completeness.percent !== undefined && day.completeness.percent < COMPLETENESS_TARGET,
    ).length,
    daysInUse: days.filter((day) => day.dailyUse.producers > 0).length,
  };
}

/** A percentage as shown: `96.5%`, or a dash when there is nothing to measure. */
export function percentText(percent: number | undefined): string {
  return percent === undefined ? "—" : `${percent}%`;
}
